import { useFormContext } from 'react-hook-form';
import { PaymentMethod } from '../common/constants';
import { formatCurrency } from '../utils/numberUtil';
import IconBank from '../icons/IconBank';
import IconQRCode from '../icons/IconQRCode';
import IconWallet from '../icons/IconWallet';
import IconCreditCard from '../icons/IconCreditCard';
import { StyledPaymentMethodForm, StyledPaymentIcon } from '../pages/Review.styled';
import { Form } from 'react-bootstrap';
import { useMemo } from 'react';
import Feedback from 'react-bootstrap/esm/Feedback';
import { orderBy } from 'lodash';
import { IPaymentSetting } from '../types/paymentTypes';
import { IHotelPaymentOption } from '../types/hotelTypes';
import { getDepositAmountVnd } from '../utils/paymentOptionUtils';

export interface IPaymentFormState {
  paymentMethod: PaymentMethod;
}

export interface IPaymentFormProps {
  onChange?: (e) => void;
  webPaymentServiceId?: string;
  paymentOptions?: IHotelPaymentOption[];
}

const getPaymentIcon = (type: PaymentMethod) => {
  switch (type) {
    case PaymentMethod.VietQR:
    case PaymentMethod.VietQR_Cake_Email:
    case PaymentMethod.VietQR_Timo_Email:
    case PaymentMethod.VietQR_VietinBank:
    case PaymentMethod.VNPAY_QR:
      return <IconQRCode />;

    case PaymentMethod.VNPAY_BankTransfer:
      return <IconBank />;
    case PaymentMethod.VNPAY_IntCard:
      return <IconCreditCard />;
    case PaymentMethod.SecurityDeposit:
    default:
      return <IconWallet />;
  }
};

const mapHotelPaymentOptions = (
  paymentOptions: IHotelPaymentOption[] | undefined,
  webPaymentServiceId?: string,
): IPaymentSetting[] => {
  if (!paymentOptions?.length) {
    return [
      {
        isActive: true,
        order: 1,
        description: 'Thanh toán khi nhận phòng',
        type: PaymentMethod.CheckIn,
      },
      ...(webPaymentServiceId
        ? [
            {
              isActive: true,
              order: 2,
              description: 'Quét mã QR trên ứng dụng ngân hàng',
              type: PaymentMethod.VietQR,
            } satisfies IPaymentSetting,
          ]
        : []),
    ];
  }

  return paymentOptions.flatMap<IPaymentSetting>((option) => {
    if (!option.isActive) {
      return [];
    }

    const getDefaultDescription = () => {
      switch (option.type) {
        case PaymentMethod.CheckIn:
          return 'Thanh toán khi nhận phòng';
        case PaymentMethod.VietQR:
          return 'Quét mã QR trên ứng dụng ngân hàng';
        case PaymentMethod.SecurityDeposit:
          return `Thanh toán khi nhận phòng (cọc ${getDepositAmountVnd([option]).toLocaleString('vi-VN')} VNĐ)`;
        default:
          return option.type;
      }
    };

    const description = option.description?.trim() || getDefaultDescription();

    switch (option.type) {
      case PaymentMethod.CheckIn:
        return [
          {
            isActive: true,
            order: option.order ?? 1,
            description,
            type: PaymentMethod.CheckIn,
          },
        ];
      case PaymentMethod.VietQR:
        if (!(option.paymentServiceId || webPaymentServiceId)) {
          return [];
        }

        return [
          {
            isActive: true,
            order: option.order ?? 2,
            description,
            type: PaymentMethod.VietQR,
          },
        ];
      case PaymentMethod.SecurityDeposit:
        if (!(option.paymentServiceId || webPaymentServiceId)) {
          return [];
        }

        return [
          {
            isActive: true,
            order: option.order ?? 3,
            description,
            type: PaymentMethod.SecurityDeposit,
          },
        ];
      default:
        return [];
    }
  });
};

export const PaymentForm = ({ onChange, webPaymentServiceId, paymentOptions }: IPaymentFormProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<IPaymentFormState>();

  const availablePaymentMethods = useMemo(
    () => mapHotelPaymentOptions(paymentOptions, webPaymentServiceId),
    [paymentOptions, webPaymentServiceId],
  );
  const sortedPayments = useMemo(
    () => orderBy(availablePaymentMethods, (item) => item.order),
    [availablePaymentMethods],
  );

  return (
    <StyledPaymentMethodForm>
      <Form.Group className={`mb-2 ${errors.paymentMethod ? 'is-invalid' : ''}`}>
        <Form.Label>Anh chị có thể thanh toán trước bằng</Form.Label>
        {!sortedPayments?.length && (
          <Form.Text className="text-danger">Khách sạn hiện chưa cấu hình phương thức thanh toán khả dụng.</Form.Text>
        )}
        {sortedPayments?.map((item) => (
          <Form.Check
            key={item.type}
            type="radio"
            isInvalid={!!errors.paymentMethod}
            id={item.type}
            name="paymentMethod"
            feedbackType="invalid"
            value={item.type}
            {...register('paymentMethod', {
              onChange,
              required: true,
            })}
            label={
              <div>
                <StyledPaymentIcon>{getPaymentIcon(item.type)}</StyledPaymentIcon>
                {item.description}
              </div>
            }
          />
        ))}
      </Form.Group>
      <Feedback type="invalid">Vui lòng chọn phương thức thanh toán</Feedback>
    </StyledPaymentMethodForm>
  );
};
